import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { i18next } from '@yola/ws-sdk';
import i18n from 'yola-editor/src/js/modules/i18n';
import getCountryOptions from '../helpers/get-country-options';
import validators from '../helpers/validators';

const {
  Paragraph,
  Heading,
  ControlGroup,
  InputGroupField,
  SimpleSelectField,
  Stack,
  HorizontalStack,
  Box,
  ActionButton,
  Mark,
} = designSystem;
const { useForm } = reactHookForm;

const DomainRegistrationForm = React.forwardRef(
  ({ initialFormData, terms = '', onSubmit, onCancel }, ref) => {
    const locale = useSelector(i18n.selectors.getLocale);
    const countryOptions = getCountryOptions(locale);

    const { control, formState, trigger, getValues } = useForm({
      defaultValues: initialFormData,
      mode: 'onTouched',
    });
    const { isValid } = formState;

    const validation = {
      email: validators.email(i18next.t('Invalid email')),
      phone: validators.phone(i18next.t('Invalid phone')),
      fax: validators.phone(i18next.t('Invalid fax')),
      required: validators.required(i18next.t('This field is required')),
    };

    const onSubmitForm = () => {
      const values = getValues();

      if (!isValid) {
        trigger();
        return;
      }

      onSubmit(values);
    };

    const handleKeyDown = (event, submitForm) => {
      if (event.key === 'Enter') {
        submitForm();
      }
    };

    return (
      <form
        ref={ref}
        className="domain-registration"
        onSubmit={onSubmitForm}
        onKeyDown={(event) => handleKeyDown(event, onSubmitForm)}
      >
        <Paragraph appearance="white" align="center">
          {i18next.t('Domain registration')}
        </Paragraph>
        <Heading type="heading-5" appearance="white" align="center">
          {i18next.t('Add contact details for your domain’s official registrant (owner)')}
        </Heading>
        <div className="domain-registration__item">
          <Stack gap="spacing-l">
            <Stack gap="spacing-xs">
              <Heading type="heading-4">{i18next.t('Personal information')}</Heading>
              <ControlGroup title={i18next.t('First name')}>
                <InputGroupField
                  control={control}
                  name="firstName"
                  rules={{
                    validate: validation.required,
                  }}
                  placeholder={i18next.t('Enter your name')}
                />
              </ControlGroup>
              <ControlGroup title={i18next.t('Last name')}>
                <InputGroupField
                  control={control}
                  name="lastName"
                  rules={{
                    validate: validation.required,
                  }}
                  placeholder={i18next.t('Enter your last name')}
                />
              </ControlGroup>
              <ControlGroup title={i18next.t('Company name')}>
                <InputGroupField
                  control={control}
                  name="orgName"
                  rules={{
                    validate: validation.required,
                  }}
                  placeholder={i18next.t('Enter your company name')}
                />
              </ControlGroup>
            </Stack>

            <Stack gap="spacing-xs">
              <Heading type="heading-4">{i18next.t('Contact information')}</Heading>
              <ControlGroup title={i18next.t('Email')}>
                <InputGroupField
                  control={control}
                  name="email"
                  rules={{
                    validate: (value) => validation.required(value) || validation.email(value),
                  }}
                  placeholder={i18next.t('Enter your email')}
                />
              </ControlGroup>
              <div className="domain-registration__input-pair">
                <div className="domain-registration__input-wrapper">
                  <ControlGroup title={i18next.t('Phone')}>
                    <InputGroupField
                      control={control}
                      name="phone"
                      rules={{
                        validate: (value) => validation.required(value) || validation.phone(value),
                      }}
                      placeholder={i18next.t('Enter your phone')}
                    />
                  </ControlGroup>
                </div>

                <div className="domain-registration__input-wrapper">
                  <ControlGroup
                    title={
                      <>
                        {i18next.t('Fax')}{' '}
                        <Mark size="small" appearance="medium-emphasis">
                          ({i18next.t('optional')})
                        </Mark>
                      </>
                    }
                  >
                    <InputGroupField
                      control={control}
                      name="fax"
                      rules={{
                        validate: validation.fax,
                      }}
                      placeholder={i18next.t('Enter your fax number')}
                    />
                  </ControlGroup>
                </div>
              </div>
            </Stack>

            <Stack gap="spacing-xs">
              <Heading type="heading-4">{i18next.t('Address')}</Heading>
              <ControlGroup title={i18next.t('Billing address')}>
                <InputGroupField
                  control={control}
                  name="address1"
                  rules={{
                    validate: validation.required,
                  }}
                  placeholder={i18next.t('Enter your address')}
                />
              </ControlGroup>

              <div className="domain-registration__input-pair">
                <div className="domain-registration__input-wrapper">
                  <ControlGroup title={i18next.t('City')}>
                    <InputGroupField
                      control={control}
                      name="city"
                      rules={{
                        validate: validation.required,
                      }}
                      placeholder={i18next.t('Enter your city')}
                    />
                  </ControlGroup>
                </div>

                <div className="domain-registration__input-wrapper">
                  <ControlGroup
                    title={
                      <>
                        {i18next.t('State')}{' '}
                        <Mark size="small" appearance="medium-emphasis">
                          ({i18next.t('optional')})
                        </Mark>
                      </>
                    }
                  >
                    <InputGroupField
                      control={control}
                      name="state"
                      placeholder={i18next.t('Enter your state')}
                    />
                  </ControlGroup>
                </div>
              </div>
              <div className="domain-registration__input-pair">
                <div className="domain-registration__input-wrapper">
                  <ControlGroup title={i18next.t('Country')}>
                    <SimpleSelectField
                      control={control}
                      name="country"
                      options={countryOptions}
                      rules={{
                        validate: validation.required,
                      }}
                      placeholder={i18next.t('Select your country')}
                    />
                  </ControlGroup>
                </div>

                <div className="domain-registration__input-wrapper">
                  <ControlGroup title={i18next.t('Postal code')}>
                    <InputGroupField
                      control={control}
                      name="postalCode"
                      rules={{
                        validate: validation.required,
                      }}
                      placeholder={i18next.t('Enter your postal code')}
                    />
                  </ControlGroup>
                </div>
              </div>
            </Stack>
          </Stack>
        </div>
        {!!terms && (
          <Box marginBottom="spacing-l">
            <Paragraph align="center" appearance="white">
              <span
                // eslint-disable-next-line yola/react/no-danger
                dangerouslySetInnerHTML={{ __html: terms }}
              />
            </Paragraph>
          </Box>
        )}
        <div className="domain-registration__buttons-container">
          <HorizontalStack gap="spacing-2-xs">
            <ActionButton
              appearance="cta"
              format="solid"
              size="large"
              onClick={onSubmitForm}
              label={i18next.t('Next')}
            />
            <ActionButton size="large" onClick={onCancel} label={i18next.t('Cancel')} />
          </HorizontalStack>
        </div>
      </form>
    );
  }
);

DomainRegistrationForm.displayName = 'DomainRegistrationForm';

DomainRegistrationForm.propTypes = {
  initialFormData: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    orgName: PropTypes.string.isRequired,
    address1: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    postalCode: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    fax: PropTypes.string,
    email: PropTypes.string.isRequired,
  }).isRequired,
  terms: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default DomainRegistrationForm;
